import React from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Paper,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
} from "@mui/material";

interface Question {
  name: string;
  question: string;
  options: string[];
}

interface FormData {
  [key: string]: string;
}

const questionsData: Question[] = [
  {
    name: "question1",
    question: "1. What does the 'p' in the 5 P's stand for?",
    options: ["Power", "Purpose", "Patience", "Practice"],
  },
  {
    name: "question2",
    question:
      "2. Which of the following is a good example of taking initiative on the job?",
    options: [
      "Waiting for your supervisor to assign every task",
      "Ignoring tasks that aren't in your job description",
      "Organizing the supply closet without being asked",
      "Showing up late but staying longer",
    ],
  },
  {
    name: "question3",
    question: "3. What should a SMART goal be?",
    options: [
      "Super, Motivating, Awesome, Real, Tough",
      "Specific, Measurable, Achievable, Relevant, Time-bound",
      "Simple, Minimal, Aligned, Realistic, Time-saving",
      "Steady, Motivational, Appropriate, Reliable, True",
    ],
  },
  {
    name: "question4",
    question: "4. Why is it important to write down your goals?",
    options: [
      "To share them with your friends",
      "So your supervisor can monitor them",
      "To stay focused and track your progress",
      "To get a higher salary",
    ],
  },
  {
    name: "question5",
    question: "5. Which of the following is an example of a short-term goal?",
    options: [
      "Buying a house in 10 years",
      "Starting your own company",
      "Learning how to use Microsoft Excel this summer",
      "Retiring at age 55",
    ],
  },
  {
    name: "question6",
    question:
      "6. What should you do if you don't understand a task at your job?",
    options: [
      "Guess and hope it works out",
      "Ignore it and move on",
      "Ask your supervisor for clarification",
      "Complain to your coworkers",
    ],
  },
  {
    name: "question7",
    question: "7. What is a sign of professionalism at work?",
    options: [
      "Using slang with your boss",
      "Checking your phone during work",
      "Being on time and dressing appropriately",
      "Leaving early when your tasks are done",
    ],
  },
  {
    name: "question8",
    question:
      "8. Who is responsible for tracking your daily work hours in MBSYEP?",
    options: [
      "Your host organization",
      "The program director",
      "You, the Youth participant",
      "Your school",
    ],
  },
  {
    name: "question9",
    question: "9. How should you act when receiving feedback at work?",
    options: [
      "Argue back",
      "Take it personally",
      "Listen carefully and improve",
      "Ignore it if you disagree",
    ],
  },
  {
    name: "question10",
    question: "10. What happens when you complete the orientation?",
    options: [
      "You get paid immediately",
      "You are guaranteed a permanent job",
      "You earn a digital badge and move to the next step",
      "Nothing—it's just for practice",
    ],
  },
];

// Create schema with proper typing
const schemaShape = questionsData.reduce((acc, { name }) => {
  acc[name] = yup.string().required("This field is required");
  return acc;
}, {} as Record<string, yup.StringSchema>);

const schema = yup.object().shape(schemaShape);

// Create default values with proper typing
const defaultValues = questionsData.reduce((acc, { name }) => {
  acc[name] = "";
  return acc;
}, {} as FormData);

export default function Test() {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: FormData) => {
    console.log("Test Results:", data);
    // Add your submission logic here
    // For example: API call to submit test results
  };

  const handleReAttempt = () => {
    reset(defaultValues);
  };

  return (
    <div className="p-16 flex justify-center min-h-screen bg-gray-50">
      <Paper className="p-32 max-w-4xl w-full bg-white rounded-2xl shadow-lg">
        <Typography
          variant="h4"
          className="mb-24 text-center font-semibold text-gray-800"
        >
          Orientation Assessment
        </Typography>

        <Typography variant="body1" className="mb-32 text-center text-gray-600">
          Please answer all questions to complete your orientation assessment.
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-24">
          {questionsData.map(({ name, question, options }) => (
            <FormControl
              component="fieldset"
              key={name}
              error={!!errors[name]}
              fullWidth
              className="bg-gray-50 p-16 rounded-xl"
            >
              <FormLabel
                component="legend"
                className="font-semibold text-gray-800 mb-12"
              >
                {question}
              </FormLabel>
              <Controller
                name={name}
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} className="ml-8">
                    {options.map((opt) => (
                      <FormControlLabel
                        key={opt}
                        value={opt}
                        control={
                          <Radio
                            sx={{
                              color: "#6b7280",
                              "&.Mui-checked": {
                                color: "#3b82f6",
                              },
                            }}
                          />
                        }
                        label={
                          <Typography className="text-gray-700">
                            {opt}
                          </Typography>
                        }
                        className="mb-8"
                      />
                    ))}
                  </RadioGroup>
                )}
              />
              {errors[name] && (
                <Typography
                  color="error"
                  variant="caption"
                  className="mt-8 font-medium"
                >
                  {errors[name]?.message}
                </Typography>
              )}
            </FormControl>
          ))}

          <div className="flex justify-end gap-12 mt-32 pt-24 border-t border-gray-200">
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReAttempt}
              className="px-24 py-12 font-semibold"
            >
              Re-Attempt
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={!isValid}
              className="px-24 py-12 font-semibold"
            >
              Submit Assessment
            </Button>
          </div>
        </form>
      </Paper>
    </div>
  );
}
